import isEmpty from "lodash/isEmpty";
import { RiCloseFill } from "react-icons/ri";
import { BsCheckAll } from "react-icons/bs";
import { MdFlag } from "react-icons/md";
import { Button, ErrorState, LoadingSpinner, Modal } from "@/components";
import { LessonDetaiBlock } from "./LessonDetailBlock";
import { useLessonDetail } from "../hooks/useLessonDetail";
import { ESTATUS_LEARNING, LessonDto, QuizDto, VISIBILITY } from "@/types";
import { useLessonCompleted } from "../hooks/useLessonCompleted";
import { QuizModal } from "./QuizModal";

interface Props {
  lesson: LessonDto | undefined;
  open: boolean;
  onClose: () => void;
}

export const Lesson = ({ lesson, open, onClose }: Props) => {
  const { data, isLoading, error, refetch } = useLessonDetail({
    id: lesson?.id,
    enabled: !!Number(lesson?.id) && open,
  });

  const { markLessonCompleted } = useLessonCompleted(lesson?.id || 0);

  const isMarkDone = data?.progress_status === ESTATUS_LEARNING.COMPLETED;
  const hasQuiz = !!data?.quiz_id && data?.quiz?.status !== VISIBILITY.private;
  const isQuizDone = data?.quiz_status === ESTATUS_LEARNING.COMPLETED;
  const navigateQuizButtonStyles: any = isMarkDone
    ? { buttonType: "primary" }
    : { variant: "outlined", color: "primary" };

  const onMarkLessonCompleted = () => {
    markLessonCompleted.mutate();
  };

  return (
    <>
      {open && (
        <Modal
          open={open}
          fullScreen
          className="bg-grey-200"
          title={
            <div className="flex gap-4">
              <button onClick={onClose} className="self-start flex-shrink-0">
                <RiCloseFill className="size-6 text-action_light_active" />
              </button>
              <div className="space-y-1">
                <p className="text-sm font-semibold leading-5.5">学習詳細</p>
                <p className="text-xs text-light_secondary font-normal leading-4.5">
                  どの動画からでも視聴可能です。視聴やテストは繰り返し受けることが可能です。
                </p>
              </div>
            </div>
          }
        >
          {isLoading && (
            <div className="w-full h-full flex items-center justify-center bg-white/50">
              <LoadingSpinner />
            </div>
          )}

          {!isEmpty(error) && (
            <ErrorState onCancel={onClose} onRetry={refetch} />
          )}

          {data?.lessonDetail?.length && (
            <div className="flex flex-col gap-y-4 justify-between min-h-full">
              <div className="space-y-4">
                {data.lessonDetail.map((block) => {
                  return (
                    <LessonDetaiBlock key={block.id} block={block as any} />
                  );
                })}
              </div>
              <div className="space-y-4">
                <div className="w-1/2 mx-auto h-[1px] bg-grey-300" />
                {isMarkDone ? (
                  <div className="!h-12 mx-auto flex justify-center items-center text-cyan gap-2">
                    <BsCheckAll size={24} />
                    <p className="font-bold leading-6.5">
                      すでに勉強済みです。
                    </p>
                  </div>
                ) : (
                  <Button
                    buttonType="primary"
                    className="!h-12 !w-[8.5rem] mx-auto"
                    iconLeft={<BsCheckAll size={24} />}
                    disabled={markLessonCompleted?.isPending}
                    onClick={onMarkLessonCompleted}
                  >
                    学習済み
                  </Button>
                )}
                {hasQuiz &&
                  (isQuizDone ? (
                    <div className="!h-12 mx-auto flex justify-center items-center text-cyan gap-2">
                      <BsCheckAll size={24} />
                      <p className="font-bold leading-6.5">
                        すでに勉強済みです。
                      </p>
                    </div>
                  ) : (
                    <QuizModal
                      quiz={{ id: data?.quiz_id || 0 } as QuizDto}
                      trigger={
                        <Button
                          {...navigateQuizButtonStyles}
                          className="!h-12 !w-[8.5rem] mx-auto"
                          iconLeft={<MdFlag size={24} />}
                        >
                          テスト開始
                        </Button>
                      }
                    />
                  ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};
